import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Folder, FolderOpen, FileText, UploadSimple, Eye, DownloadSimple,
  Trash, EnvelopeSimple, User as UserIcon, Warning, ArrowsClockwise,
} from "@phosphor-icons/react";

/**
 * Membership Applications module.
 *
 * Layout: month/year folder list on the left, application list (of the
 * open folder) in the middle, and a details drawer on the right that
 * lets the admin preview attachments, attach the application to an
 * existing member, or discard it.
 *
 * Data source: `/api/applications/*` (folders → list → detail). Every
 * folder shows total + new counts; applications remain in their bucket
 * until attached, at which point they're copied to the member's file
 * and the counts refresh in the same round-trip.
 */

function monthYearLabel(key) {
  try {
    const [y, m] = key.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch { return key; }
}

function FolderList({ folders, activeKey, onPick }) {
  return (
    <div className="surface-grid overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="tab-tag text-slate-500">Folders</div>
        <span className="text-[10px] mono text-slate-500">{folders.length}</span>
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {folders.length === 0 ? (
          <div className="p-4 text-xs text-slate-500 italic text-center">No pending applications.</div>
        ) : folders.map((f) => {
          const active = f.month_year === activeKey;
          return (
            <button
              key={f.month_year}
              type="button"
              onClick={() => onPick(f.month_year)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-sm ${active ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}
              data-testid={`apps-folder-${f.month_year}`}
            >
              {active ? <FolderOpen size={14} weight="fill" /> : <Folder size={14} weight="fill" className="text-amber-600" />}
              <span className="font-medium flex-1">{f.label || monthYearLabel(f.month_year)}</span>
              <span className="flex items-center gap-1">
                {f.new > 0 && (
                  <Badge className="rounded-sm bg-red-600 text-white text-[10px] px-1.5 py-0" data-testid={`apps-folder-new-${f.month_year}`}>
                    {f.new} NEW
                  </Badge>
                )}
                <Badge className={`rounded-sm text-[10px] px-1.5 py-0 ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`apps-folder-total-${f.month_year}`}>
                  {f.total}
                </Badge>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApplicationList({ apps, activeKey, onOpen, activeAppId }) {
  return (
    <div className="surface-grid overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div>
          <div className="tab-tag text-slate-500">Applications</div>
          <div className="text-xs font-semibold text-slate-900">{activeKey ? monthYearLabel(activeKey) : "Pick a folder"}</div>
        </div>
        <Badge className="rounded-sm bg-slate-100 text-slate-700 text-[10px]">{apps.length}</Badge>
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {!activeKey ? (
          <div className="p-4 text-xs text-slate-500 italic text-center">Pick a folder to see applications.</div>
        ) : apps.length === 0 ? (
          <div className="p-4 text-xs text-slate-500 italic text-center">No applications in this folder.</div>
        ) : apps.map((a) => {
          const active = a.id === activeAppId;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpen(a.id)}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 text-xs ${active ? "bg-blue-50 border-blue-300" : "hover:bg-slate-50"}`}
              data-testid={`apps-list-${a.id}`}
            >
              <div className="flex items-center gap-2">
                {a.source === "email" ? <EnvelopeSimple size={12} weight="fill" className="text-blue-600" /> : <FileText size={12} weight="fill" className="text-slate-500" />}
                <span className="font-semibold text-slate-900 truncate">{a.applicant_name}</span>
                {a.status === "new" && <Badge className="rounded-sm bg-red-600 text-white text-[9px] ml-auto">NEW</Badge>}
              </div>
              <div className="text-[10px] text-slate-500 mono mt-0.5 truncate">
                {new Date(a.received_at).toLocaleString()}
                {a.attachments?.length > 0 && ` · ${a.attachments.length} file(s)`}
              </div>
              {a.subject && <div className="text-[10px] text-slate-600 truncate mt-0.5">{a.subject}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApplicationDetail({ app, onAttach, onDiscard }) {
  if (!app) {
    return (
      <div className="surface-grid p-6 text-center text-sm text-slate-500 italic" data-testid="apps-detail-empty">
        Select an application to preview attachments, attach it to a member, or discard it.
      </div>
    );
  }
  return (
    <div className="surface-grid" data-testid={`apps-detail-${app.id}`}>
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-slate-900 text-base truncate">{app.applicant_name}</div>
          <div className="text-[11px] text-slate-500 mono truncate">
            {app.applicant_email || "no email on record"}
          </div>
          <div className="text-[10px] text-slate-500 mono mt-0.5">
            Received {new Date(app.received_at).toLocaleString()} · Source: {app.source}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button
            size="sm"
            onClick={onAttach}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
            data-testid="apps-attach-open"
          >
            <UserIcon size={12} weight="bold" className="mr-1" /> Upload to File
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDiscard}
            className="rounded-sm h-8 text-xs border-red-300 text-red-700 hover:bg-red-50"
            data-testid="apps-discard"
          >
            <Trash size={12} weight="bold" className="mr-1" /> Discard
          </Button>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {app.subject && (
          <div>
            <div className="tab-tag text-slate-500">Subject</div>
            <div className="text-sm">{app.subject}</div>
          </div>
        )}
        {app.body_text && (
          <div>
            <div className="tab-tag text-slate-500">Message</div>
            <pre className="text-xs whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-sm p-2 max-h-52 overflow-y-auto font-sans">{app.body_text}</pre>
          </div>
        )}
        <div>
          <div className="tab-tag text-slate-500 mb-1.5">Attachments ({app.attachments?.length || 0})</div>
          {(app.attachments || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic">No attachments.</div>
          ) : (
            <div className="space-y-1.5">
              {app.attachments.map((a, i) => (
                <div key={i} className="flex items-center gap-2 border border-slate-200 bg-white rounded-sm px-2 py-1.5 text-xs" data-testid={`apps-att-${i}`}>
                  <FileText size={14} weight="fill" className="text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{a.filename}</div>
                    <div className="text-[10px] text-slate-500 mono">{a.content_type} · {(a.size / 1024).toFixed(1)}KB</div>
                  </div>
                  <Button asChild size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" data-testid={`apps-att-preview-${i}`}>
                    <a href={`${API_BASE}/applications/${app.id}/attachments/${i}/preview`} target="_blank" rel="noreferrer">
                      <Eye size={11} weight="bold" className="mr-1" /> Preview
                    </a>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" data-testid={`apps-att-download-${i}`}>
                    <a href={`${API_BASE}/applications/${app.id}/attachments/${i}/download`}>
                      <DownloadSimple size={11} weight="bold" className="mr-1" /> Download
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AttachDialog({ app, users, onCancel, onDone }) {
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (app) { setQ(""); setPick(""); setConfirmOpen(false); }
  }, [app]);

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users]);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? sorted.filter((u) => `${u.name} ${u.email} ${(u.certifications || []).join(" ")} ${u.role}`.toLowerCase().includes(needle))
    : sorted;
  const picked = users.find((u) => u.id === pick);

  async function go() {
    if (!pick || !app) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${app.id}/attach`, { user_id: pick });
      toast.success(`Attached ${data.documents_written} file(s) to ${data.member_name}`);
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally { setBusy(false); }
  }

  if (!app) return null;
  return (
    <>
      <Dialog open={!!app && !confirmOpen} onOpenChange={(o) => !o && onCancel()}>
        <DialogContent className="max-w-lg" data-testid="apps-attach-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserIcon size={16} weight="bold" /> Upload Application to Member Profile
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Attaching the application for <strong>{app.applicant_name}</strong> ({app.attachments?.length || 0} attachment(s)) to a member's file.
            </p>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="tab-tag">Search Members</Label>
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, email, role or certification…"
                className="rounded-sm"
                data-testid="apps-attach-search"
              />
            </div>
            <div className="border border-slate-200 rounded-sm max-h-64 overflow-y-auto bg-white">
              {filtered.length === 0 ? (
                <div className="text-xs text-slate-500 italic text-center p-4">No members match.</div>
              ) : filtered.map((u) => (
                <label
                  key={u.id}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs border-b border-slate-100 cursor-pointer ${pick === u.id ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                  data-testid={`apps-attach-user-${u.id}`}
                >
                  <input
                    type="radio"
                    name="apps-attach-pick"
                    checked={pick === u.id}
                    onChange={() => setPick(u.id)}
                  />
                  <span className="font-medium">{u.name}</span>
                  <Badge variant="outline" className="rounded-sm text-[10px]">{u.role}</Badge>
                  {(u.certifications || []).slice(0, 3).map((c) => (
                    <Badge key={c} className="rounded-sm bg-slate-900 text-white text-[9px]">{c}</Badge>
                  ))}
                  <span className="ml-auto text-[10px] text-slate-500 mono">{u.email}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={onCancel} className="rounded-sm">Cancel</Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!pick}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="apps-attach-review"
            >
              Review & Attach →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal — spec wording */}
      <Dialog open={!!app && confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent className="max-w-md" data-testid="apps-attach-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Warning size={16} weight="fill" /> Confirm Attachment
            </DialogTitle>
          </DialogHeader>
          <div className="pt-2 text-sm text-slate-800">
            <p>Attach the application for <strong>{app.applicant_name}</strong> to the member profile for <strong>{picked?.name}</strong>?</p>
            <p className="text-[11px] text-slate-500 mt-2">
              This will copy {app.attachments?.length || 0} attachment(s) to that member's file as auto-approved documents and remove the application from the pending folder.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} className="rounded-sm">Back</Button>
            <Button
              onClick={go}
              disabled={busy}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="apps-attach-confirm-go"
            >
              {busy ? "Attaching…" : "Yes, Attach"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ManualAddDialog({ open, setOpen, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setEmail(""); setSubject(""); setBody(""); setFiles([]); }
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error("Applicant name required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("applicant_name", name.trim());
      if (email.trim()) fd.append("applicant_email", email.trim());
      if (subject.trim()) fd.append("subject", subject.trim());
      if (body.trim()) fd.append("body_text", body.trim());
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/applications/manual", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Application filed under ${data.month_year}`);
      onCreated(data);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="apps-manual-dialog">
        <DialogHeader><DialogTitle>Add Application Manually</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="tab-tag">Applicant Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="apps-manual-name" required />
            </div>
            <div>
              <Label className="tab-tag">Applicant Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" data-testid="apps-manual-email" />
            </div>
          </div>
          <div>
            <Label className="tab-tag">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm" data-testid="apps-manual-subject" />
          </div>
          <div>
            <Label className="tab-tag">Notes / Body</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="rounded-sm min-h-[80px]" data-testid="apps-manual-body" />
          </div>
          <div>
            <Label className="tab-tag">Attachments</Label>
            <Input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="rounded-sm"
              data-testid="apps-manual-files"
            />
            {files.length > 0 && <div className="text-[10px] text-slate-500 mt-1">{files.length} file(s) selected</div>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-slate-900 text-white" data-testid="apps-manual-submit">
              {busy ? "Uploading…" : "File Application"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Applications() {
  const [folders, setFolders] = useState([]);
  const [apps, setApps] = useState([]);
  const [activeFolder, setActiveFolder] = useState("");
  const [activeApp, setActiveApp] = useState(null);
  const [users, setUsers] = useState([]);
  const [attaching, setAttaching] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  async function loadFolders(preferKey) {
    const { data } = await api.get("/applications/folders");
    setFolders(data.folders || []);
    const pick = preferKey || (data.folders?.[0]?.month_year || "");
    if (pick !== activeFolder) setActiveFolder(pick);
    return data.folders || [];
  }
  async function loadApps(key) {
    if (!key) { setApps([]); return; }
    const { data } = await api.get(`/applications/?month_year=${encodeURIComponent(key)}`);
    setApps(data || []);
  }
  async function openApp(id) {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setActiveApp(data);
      // Refresh the folder + list — /get marks the row reviewed so
      // the NEW counter should drop.
      loadFolders(activeFolder);
      loadApps(activeFolder);
      // Sidebar owns its own poll but let it know something changed so
      // the badge drops immediately rather than on the 45s tick.
      window.dispatchEvent(new Event("applications-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  }

  useEffect(() => {
    loadFolders();
    api.get("/users/").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);
  useEffect(() => { loadApps(activeFolder); setActiveApp(null); /* eslint-disable-next-line */ }, [activeFolder]);

  async function handleDiscard() {
    if (!activeApp) return;
    if (!window.confirm(`Discard the application from ${activeApp.applicant_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/applications/${activeApp.id}`);
      toast.success("Application discarded");
      setActiveApp(null);
      loadFolders(activeFolder);
      loadApps(activeFolder);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  function handleAttached(result) {
    toast.success(`Attached to ${result.member_name}'s profile`);
    setAttaching(null);
    setActiveApp(null);
    setFolders(result.folders || []);
    loadApps(activeFolder);
  }

  return (
    <AppLayout
      title="Applications"
      subtitle="Membership applications received by email or filed manually"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-sm h-9 text-xs"
            onClick={() => { loadFolders(activeFolder); loadApps(activeFolder); }}
            data-testid="apps-refresh"
          >
            <ArrowsClockwise size={13} weight="bold" className="mr-1" /> Refresh
          </Button>
          <Button className="rounded-sm bg-slate-900 text-white" onClick={() => setManualOpen(true)} data-testid="apps-add-manual">
            <UploadSimple size={13} weight="bold" className="mr-1" /> Add Application
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-[280px_320px_1fr] gap-3 items-start">
        <FolderList folders={folders} activeKey={activeFolder} onPick={setActiveFolder} />
        <ApplicationList apps={apps} activeKey={activeFolder} onOpen={openApp} activeAppId={activeApp?.id} />
        <ApplicationDetail
          app={activeApp}
          onAttach={() => setAttaching(activeApp)}
          onDiscard={handleDiscard}
        />
      </div>

      <AttachDialog
        app={attaching}
        users={users}
        onCancel={() => setAttaching(null)}
        onDone={handleAttached}
      />
      <ManualAddDialog
        open={manualOpen}
        setOpen={setManualOpen}
        onCreated={async () => { const f = await loadFolders(); if (f[0]) setActiveFolder(f[0].month_year); }}
      />
    </AppLayout>
  );
}
